import { Component, inject, Input, OnInit, signal } from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { AgendaService, BusinessService } from '../../../../services/business';
import { Agenda, Client } from '../../../../shared/interfaces';
import { BaseAuthComponent } from '../../../../shared/common/BaseAuthComponent.class';
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';
import { PeopleAddDrawer } from '../../../../shared/components/people-add-drawer/people-add-drawer';
import { FieldLabels } from '../../../../shared/common/formValidHelper';

@Component({
  selector: 'app-schedule-add',
  imports: [ReactiveFormsModule, CommonModule, PeopleAddDrawer],
  templateUrl: './schedule-add.html',
  styleUrl: './schedule-add.css',
})
export class ScheduleAdd extends BaseAuthComponent implements OnInit {
  agendaApi = inject(AgendaService);
  businessApi = inject(BusinessService);
  private fb = inject(NonNullableFormBuilder);
  @Input() agendaId: string | null = null;
  professionals = signal<Client[]>([]);
  selectedClient = signal<Client | null>(null);
  drawerOpen = false;
  isLoadingData = signal<boolean>(false);

  scheduleForm = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(3)]],
    clientId: ['', [Validators.required]],
    clientSearch: [''],
    description: [''],
    color: [
      '#465FFF',
      [
        Validators.required,
        Validators.pattern(/^#(?:[0-9a-fA-F]{3}){1,2}$/), // #RGB o #RRGGBB
      ],
    ],
  });

  ngOnInit(): void {
    if (this.agendaId) {
      this.loadAgendaById(this.agendaId);
    }
  }

  clientResults$ = this.scheduleForm.get('clientSearch')!.valueChanges.pipe(
    debounceTime(250),
    map((v) => (v ?? '').trim()),
    distinctUntilChanged(),
    switchMap((q) =>
      q.length < 2
        ? of([])
        : this.businessApi.searchEntityByBusinessId(
            this.businessId,
            'professionals',
            q
          )
    )
  );

  loadAgendaById(agendaId: string) {
    this.agendaApi.getAgenda(agendaId).subscribe({
      next: (res) => {
        this.mapAgendaToForm(res);
      },
      error: (err) => {
        console.error(err);
        this.selectedClient.set(null);
      },
    });
  }

  private mapAgendaToForm(agenda: Agenda) {
    if (!agenda) return;
    this.scheduleForm.patchValue({
      name: agenda.name,
      description: agenda.description ?? '',
      color: agenda.color ?? '#465FFF',
      clientId: agenda.professionalId!,
    });

    if (agenda.professionalId) {
      this.loadProfessionalById(agenda.professionalId);
    }
  }

  private loadProfessionalById(professionalId: string) {
    this.isLoadingData.set(true);
    this.businessApi
      .getRecordByBusinessId<Client>(
        this.businessId,
        professionalId,
        'professionals'
      )
      .subscribe({
        next: (res) => {
          const client: Client = {
            userId: res.userId,
            isActive: true,
            clientName: res.firstName + ' ' + res.lastName,
            email: res.email,
            phone: res.phone,
          };
          this.selectedClient.set(client);
          this.isLoadingData.set(false);
        },
        error: (err) => {
          console.error(err);
          this.isLoadingData.set(false);
        },
      });
  }

  selectClient(c: Client) {
    this.selectedClient.set(c);
    this.scheduleForm.patchValue({ clientId: c.userId, clientSearch: '' });
  }

  isValid(): boolean {
    const fieldLabels: FieldLabels = {
      name: 'Título',
      clientId: 'Profesional',
      color: 'Color',
    };
    this.toastFormErrors('crear el evento', this.scheduleForm, fieldLabels);
    this.markAllAndFocusFirstError(this.scheduleForm);
    this.scheduleForm.markAllAsTouched();
    return this.scheduleForm.valid;
  }

  getValue(): any {
    const { name, description, clientId, color } =
      this.scheduleForm.getRawValue();

    return {
      name,
      description,
      professionalId: clientId,
      color,
      agendaId: this.agendaId,
    };
  }

  onCreateClient(ev: PointerEvent) {
    ev.preventDefault();
    this.drawerOpen = true;
  }

  onDrawerClose(): void {
    this.drawerOpen = false;
  }

  onProfessionalCreated(client: Client): void {
    if (client) {
      this.selectedClient.set(client);
      this.scheduleForm.patchValue({
        clientId: client.userId,
      });
    }
  }

  clearClient() {
    this.selectedClient.set(null);
    this.scheduleForm.patchValue({ clientId: '', clientSearch: '' });
  }
}
